import { useEffect, useState, type FormEvent } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useTotpEnrollment } from '../hooks/useTotpEnrollment'
import { authClient } from '../lib/auth-client'
import { logError } from '../lib/logError'
import { TOURS } from '../tours/registry'
import { useTours } from '../tours/TourProvider'

const TITLE_KEY_PREFIX = 'bq_account_title:'

function titleStorageKey(email: string | null | undefined): string | null {
  const normalized = email?.trim().toLowerCase()
  return normalized ? `${TITLE_KEY_PREFIX}${normalized}` : null
}

type PasskeyRow = {
  id: string
  name?: string | null
  createdAt?: Date | string | null
}

/** Profile + TOTP + passkeys (SMS MFA retired). */
export function AccountPage() {
  const { user, updateDisplayName } = useAuth()
  const totp = useTotpEnrollment(user)
  const { isSeen, playTour, resetTour, resetAllTours } = useTours()

  const [name, setName] = useState('')
  const [title, setTitle] = useState('')
  const [profileBusy, setProfileBusy] = useState(false)
  const [profileNotice, setProfileNotice] = useState<string | null>(null)

  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [passkeys, setPasskeys] = useState<PasskeyRow[]>([])
  const [passkeyName, setPasskeyName] = useState('')

  useEffect(() => {
    setName(user?.displayName ?? '')
    const key = titleStorageKey(user?.email)
    setTitle(key ? (window.localStorage.getItem(key) ?? '') : '')
  }, [user?.displayName, user?.email])

  useEffect(() => {
    void totp.refresh()
    void loadPasskeys()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload on user change only
  }, [user?.uid])

  async function loadPasskeys() {
    try {
      const { data, error: listErr } = await authClient.passkey.listUserPasskeys()
      if (listErr) throw new Error(listErr.message)
      setPasskeys((data as PasskeyRow[]) ?? [])
    } catch (err) {
      logError('passkey.list', err)
    }
  }

  async function onSaveProfile(e: FormEvent) {
    e.preventDefault()
    if (!user) return
    setProfileBusy(true)
    setProfileNotice(null)
    setError(null)
    try {
      const trimmedName = name.trim()
      if (trimmedName !== (user.displayName ?? '')) {
        await updateDisplayName(trimmedName)
      }
      const key = titleStorageKey(user.email)
      if (key) {
        const trimmedTitle = title.trim()
        if (trimmedTitle) window.localStorage.setItem(key, trimmedTitle)
        else window.localStorage.removeItem(key)
      }
      setProfileNotice('Profile saved.')
    } catch (err) {
      logError('account.profile', err)
      setError(err instanceof Error ? err.message : "Couldn't save profile")
    } finally {
      setProfileBusy(false)
    }
  }

  async function onStartTotp() {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await totp.startEnable()
      setNotice('Scan the QR / URI in your authenticator, then enter a code.')
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't start authenticator setup")
    } finally {
      setBusy(false)
    }
  }

  async function onConfirmTotp(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await totp.confirmEnable(code.trim())
      setCode('')
      setNotice('Authenticator enabled. Save your backup codes.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code — try again')
    } finally {
      setBusy(false)
    }
  }

  async function onDisableTotp() {
    if (!window.confirm('Disable authenticator codes for sign-in?')) return
    setBusy(true)
    setError(null)
    try {
      await totp.disable()
      setNotice('Authenticator disabled.')
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't disable authenticator")
    } finally {
      setBusy(false)
    }
  }

  async function onAddPasskey(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const { error: addErr } = await authClient.passkey.addPasskey({
        name: passkeyName.trim() || 'Passkey',
      })
      if (addErr) throw new Error(addErr.message || "Couldn't add passkey")
      setPasskeyName('')
      await loadPasskeys()
      setNotice('Passkey registered.')
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't add passkey")
    } finally {
      setBusy(false)
    }
  }

  async function onDeletePasskey(id: string) {
    if (!window.confirm('Remove this passkey?')) return
    setBusy(true)
    setError(null)
    try {
      const { error: delErr } = await authClient.passkey.deletePasskey({ id })
      if (delErr) throw new Error(delErr.message || "Couldn't remove passkey")
      await loadPasskeys()
      setNotice('Passkey removed.')
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't remove passkey")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="shell">
      <header className="top compact">
        <div className="brand">
          <span className="mark">BeyondQuota</span>
          <h1>Account</h1>
        </div>
        <p className="lede tight">Profile, authenticator (TOTP), and passkeys.</p>
      </header>

      {error ? (
        <p className="banner" role="alert">
          {error}
        </p>
      ) : null}

      <main className="account-main">
        <section className="account-section" aria-labelledby="account-profile-heading">
          <h2 id="account-profile-heading" className="account-section-title">
            Profile
          </h2>
          <div className="account-panel">
            <form className="account-profile-form" onSubmit={(e) => void onSaveProfile(e)}>
              <label className="ctl">
                <span>Name</span>
                <input
                  type="text"
                  autoComplete="name"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={profileBusy}
                />
              </label>
              <label className="ctl">
                <span>Title</span>
                <input
                  type="text"
                  autoComplete="organization-title"
                  placeholder="Role or title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={profileBusy}
                />
              </label>
              <label className="ctl">
                <span>Email</span>
                <input
                  type="email"
                  className="account-readonly"
                  value={user?.email ?? ''}
                  readOnly
                  aria-readonly="true"
                />
              </label>
              <div className="account-form-actions">
                <button type="submit" className="login-primary" disabled={profileBusy}>
                  Save profile
                </button>
                {profileNotice ? (
                  <p className="login-hint" role="status">
                    {profileNotice}
                  </p>
                ) : null}
              </div>
            </form>
          </div>
        </section>

        <section className="account-section" aria-labelledby="account-totp-heading">
          <h2 id="account-totp-heading" className="account-section-title">
            Authenticator (TOTP)
          </h2>
          <div className="account-panel">
            {notice ? (
              <p className="login-hint" role="status">
                {notice}
              </p>
            ) : null}

            {totp.enabled && !totp.totpURI ? (
              <div className="account-mfa-current">
                <div className="account-mfa-current-body">
                  <span className="account-mfa-number">Authenticator enrolled</span>
                  <span className="pill muted">TOTP on magic-link sign-in</span>
                </div>
                <div className="account-mfa-actions">
                  <button
                    type="button"
                    className="ghost"
                    disabled={busy}
                    onClick={() => void onDisableTotp()}
                  >
                    Disable
                  </button>
                </div>
              </div>
            ) : null}

            {totp.totpURI ? (
              <form className="login-form" onSubmit={(e) => void onConfirmTotp(e)}>
                <p className="login-hint" role="note">
                  Add this URI in your authenticator app:
                </p>
                <code className="account-readonly" style={{ display: 'block', wordBreak: 'break-all' }}>
                  {totp.totpURI}
                </code>
                {totp.backupCodes?.length ? (
                  <p className="login-hint" role="note">
                    Backup codes (save now): {totp.backupCodes.join(', ')}
                  </p>
                ) : null}
                <label className="ctl">
                  <span>Verification code</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={busy}
                    required
                  />
                </label>
                <button type="submit" className="login-primary" disabled={busy}>
                  Confirm authenticator
                </button>
                <button
                  type="button"
                  className="ghost"
                  disabled={busy}
                  onClick={() => totp.clearSetup()}
                >
                  Cancel
                </button>
              </form>
            ) : null}

            {!totp.enabled && !totp.totpURI ? (
              <button
                type="button"
                className="login-primary"
                disabled={busy}
                onClick={() => void onStartTotp()}
              >
                Enable authenticator
              </button>
            ) : null}
          </div>
        </section>

        <section className="account-section" aria-labelledby="account-passkey-heading">
          <h2 id="account-passkey-heading" className="account-section-title">
            Passkeys
          </h2>
          <div className="account-panel">
            {passkeys.length === 0 ? (
              <p className="login-hint">No passkeys yet — add one for phishing-resistant sign-in.</p>
            ) : (
              <ul className="account-mfa-current" style={{ listStyle: 'none', padding: 0 }}>
                {passkeys.map((pk) => (
                  <li key={pk.id} className="account-mfa-current" style={{ marginBottom: '0.5rem' }}>
                    <div className="account-mfa-current-body">
                      <span className="account-mfa-number">{pk.name || 'Passkey'}</span>
                    </div>
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={() => void onDeletePasskey(pk.id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <form className="login-form" onSubmit={(e) => void onAddPasskey(e)}>
              <label className="ctl">
                <span>Passkey name</span>
                <input
                  type="text"
                  placeholder="Laptop / phone"
                  value={passkeyName}
                  onChange={(e) => setPasskeyName(e.target.value)}
                  disabled={busy}
                />
              </label>
              <button type="submit" className="login-primary" disabled={busy}>
                Add passkey
              </button>
            </form>
          </div>
        </section>

        <section className="account-section" aria-labelledby="account-tours-heading">
          <h2 id="account-tours-heading" className="account-section-title">
            Product tours
          </h2>
          <div className="account-panel">
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {TOURS.map((t) => (
                <li key={t.id} style={{ marginBottom: '0.5rem' }}>
                  <button type="button" className="linkish" onClick={() => playTour(t.id)}>
                    {t.title}
                  </button>
                  {isSeen(t) ? (
                    <button type="button" className="ghost" onClick={() => resetTour(t.id)}>
                      Reset
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
            <button type="button" className="ghost" onClick={() => resetAllTours()}>
              Reset all tours
            </button>
          </div>
        </section>
      </main>
    </div>
  )
}
